import dayjs from "dayjs";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { IWorkResource } from "../../../../common/data/dtos/work-resource";
import WorkResourceEnum from "../../../../common/data/enums/enumWorkResouce";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Select from "../../../../components/bootstrap/forms/Select";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import workResourceService from "../../../../services/work-resource-service";

type WorkResourceFormValues = {
    id?: string;
    description: string;
    approvalCertificate: string;
    durability: number;
    dueDate: string;
    brand: string;
    manufacturer: string;
    batch: string;
    type: string;
};

interface Props {
    isModalOpened: boolean;
    selectedWorkResource: IWorkResource | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const WorkResourceModal = ({ isModalOpened, selectedWorkResource, onModalClose, onAfterSubmit }: Props) => {

    const formik = useFormik({
        initialValues: {
            description: '',
            approvalCertificate: '',
            durability: 0,
            dueDate: '',
            brand: '',
            manufacturer: '',
            batch: '',
            type: '0',
        },

        validate: (values) => {
            const errors: {
                description?: string;
                type?: string;
            } = {};

            if (!values.description) errors.description = MESSAGES.VALIDATION.REQUIRED_FIELD;

            if (!values.type) errors.type = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        resetForm();

        if (selectedWorkResource) setFieldValues(selectedWorkResource);
    }, [isModalOpened]);

    const onFormSubmit = async (values: WorkResourceFormValues) => {
        if (selectedWorkResource) {
            setIsSubmitting(true);

            const response = await workResourceService.updateWorkResource({
                id: selectedWorkResource.id,
                description: values.description,
                approvalCertificate: values.approvalCertificate,
                durability: values.durability,
                dueDate: values.dueDate ? dayjs(values.dueDate).toDate() : undefined,
                brand: values.brand,
                manufacturer: values.manufacturer,
                batch: values.batch,
                type: values.type === '0' ? WorkResourceEnum.tool : WorkResourceEnum.personalProtectiveEquipment,
                rowVersion: selectedWorkResource.rowVersion,
                status: selectedWorkResource.status
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await workResourceService.createWorkResource({
                description: values.description,
                approvalCertificate: values.approvalCertificate,
                durability: values.durability,
                dueDate: values.dueDate ? dayjs(values.dueDate).toDate() : undefined,
                brand: values.brand,
                manufacturer: values.manufacturer,
                batch: values.batch,
                type: values.type === '0' ? WorkResourceEnum.tool : WorkResourceEnum.personalProtectiveEquipment,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                description: '',
                approvalCertificate: '',
                durability: 0,
                dueDate: '',
                brand: '',
                manufacturer: '',
                batch: '',
                type: '0',
            },
        });
    };

    const setFieldValues = (values: IWorkResource) => {
        formik.setFieldValue('description', values.description || '');
        formik.setFieldValue('approvalCertificate', values.approvalCertificate || '');
        formik.setFieldValue('durability', values.durability || 0);
        formik.setFieldValue('dueDate', values.dueDate ? dayjs(values.dueDate).format('YYYY-MM-DD') : '');
        formik.setFieldValue('brand', values.brand || '');
        formik.setFieldValue('manufacturer', values.manufacturer || '');
        formik.setFieldValue('batch', values.batch || '');
        formik.setFieldValue('type', values.type === WorkResourceEnum.tool ? '0' : '1');
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='work-resource-modal'>Manutenção de ferramentas e EPI</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>

                    <CardTabItem id='tab-item-1' title='Dados de ferramenta e EPI' icon='Build'>

                        <div className='row g-4'>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='description' label='Descrição' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.description}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.description}
                                        invalidFeedback={formik.errors.description}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='approvalCertificate' label='CA' isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.approvalCertificate} />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='batch' label='Lote' isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.batch} />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='durability' label='Durabilidade (Dias)' isFloating>
                                    <Input type="number" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.durability} />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='dueDate' label='Data Vencimento' isFloating>
                                    <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={ formik.values.dueDate } />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='brand' label='Marca' isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.brand} />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup id='manufacturer' label='Fabricante' isFloating>
                                    <Input onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.manufacturer} />
                                </FormGroup>
                            </div>

                            <div className='col-lg-6 col-md-6 col-sm-12'>
                                <FormGroup
                                    id='type'
                                    label='Tipo'
                                    isFloating
                                >
                                    <Select
                                        ariaLabel='Escolha um tipo'
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.type}
                                        list={
                                            [
                                                { value: '0', text: 'Ferramenta' },
                                                { value: '1', text: 'EPI' }
                                            ]
                                        }
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.type}
                                        invalidFeedback={
                                            formik.errors.type
                                        }
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>

                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};